import {
    SUBSCRIPTION_STATUS,
    SUBSCRIPTION_STATUS_LABELS,
    normalizeSubscriptionStatus,
} from './subscriptionStatus';

/**
 * Status canônicos da assinatura do clube (plano Eurobem).
 * Espelha ResolveSubscriptionStatusService no backend.
 */
export const CLUB_SUBSCRIPTION_STATUS = SUBSCRIPTION_STATUS;

export const CLUB_SUBSCRIPTION_STATUS_LABELS = SUBSCRIPTION_STATUS_LABELS;

export function normalizeClubSubscriptionStatus(status) {
    return normalizeSubscriptionStatus(status);
}

export function formatClubDueDate(dueDateStr) {
    if (!dueDateStr) return null;
    const dueDate = new Date(dueDateStr);
    if (Number.isNaN(dueDate.getTime())) return null;
    return dueDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function getClubDueDateDiffDays(dueDateStr, now = new Date()) {
    if (!dueDateStr) return null;
    const dueDate = new Date(dueDateStr);
    if (Number.isNaN(dueDate.getTime())) return null;

    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Status da assinatura para badges/avisos na página Conta.
 */
export function getClubSubscriptionStatusInfo(signature) {
    if (!signature) {
        return {
            code: null,
            label: 'Sem assinatura',
            tone: 'neutral',
            color: 'default',
            description: null,
            accessAllowed: false,
            blocked: true,
        };
    }

    const code = normalizeClubSubscriptionStatus(signature.status);
    const label = CLUB_SUBSCRIPTION_STATUS_LABELS[code] || code;
    const accessAllowed = signature.accessAllowed === true || code === CLUB_SUBSCRIPTION_STATUS.ACTIVE;
    const blocked =
        signature.blocked === true ||
        [
            CLUB_SUBSCRIPTION_STATUS.WAITING,
            CLUB_SUBSCRIPTION_STATUS.EXPIRED,
            CLUB_SUBSCRIPTION_STATUS.CANCELLED,
            CLUB_SUBSCRIPTION_STATUS.INACTIVE,
            CLUB_SUBSCRIPTION_STATUS.SUSPENDED,
        ].includes(code);

    const byCode = {
        [CLUB_SUBSCRIPTION_STATUS.ACTIVE]: {
            tone: 'success',
            color: 'success',
            description: null,
        },
        [CLUB_SUBSCRIPTION_STATUS.WAITING]: {
            tone: 'warning',
            color: 'warning',
            description: 'Assinatura aguardando confirmação de pagamento.',
        },
        [CLUB_SUBSCRIPTION_STATUS.EXPIRED]: {
            tone: 'danger',
            color: 'error',
            description: 'Há cobranças em atraso. Regularize o pagamento para reativar o clube.',
        },
        [CLUB_SUBSCRIPTION_STATUS.CANCELLED]: {
            tone: 'danger',
            color: 'error',
            description: 'Assinatura cancelada. O clube e seus serviços estão desativados.',
        },
        [CLUB_SUBSCRIPTION_STATUS.INACTIVE]: {
            tone: 'danger',
            color: 'error',
            description: 'Assinatura inativa. O ciclo foi encerrado.',
        },
        [CLUB_SUBSCRIPTION_STATUS.SUSPENDED]: {
            tone: 'warning',
            color: 'warning',
            description: 'Assinatura suspensa por contestação no cartão.',
        },
    };

    const meta = byCode[code] || { tone: 'neutral', color: 'default', description: null };

    return {
        code,
        label,
        accessAllowed,
        blocked,
        ...meta,
    };
}

/**
 * Chip do header do gestor: prioriza Signature.status; em ACTIVE mostra countdown do vencimento.
 */
export function getClubPlanHeaderStatus(signature, { now = new Date() } = {}) {
    if (!signature) {
        return {
            tone: 'neutral',
            message: 'Sem assinatura',
            code: null,
        };
    }

    const code = normalizeClubSubscriptionStatus(signature.status);
    const formattedDate = formatClubDueDate(signature.dueDateCycle);
    const diffDays = getClubDueDateDiffDays(signature.dueDateCycle, now);

    if (code === CLUB_SUBSCRIPTION_STATUS.WAITING) {
        return { tone: 'warning', message: 'Assinatura aguardando pagamento', code };
    }

    if (code === CLUB_SUBSCRIPTION_STATUS.EXPIRED) {
        return {
            tone: 'danger',
            message: 'Assinatura expirada · cobrança em atraso',
            code,
        };
    }

    if (code === CLUB_SUBSCRIPTION_STATUS.CANCELLED) {
        return {
            tone: 'danger',
            message: formattedDate ? `Assinatura cancelada · ciclo até ${formattedDate}` : 'Assinatura cancelada',
            code,
        };
    }

    if (code === CLUB_SUBSCRIPTION_STATUS.INACTIVE) {
        return { tone: 'danger', message: 'Assinatura inativa', code };
    }

    // ACTIVE (ou legado): countdown pelo dueDateCycle
    if (diffDays == null) {
        return { tone: 'success', message: 'Assinatura ativa', code };
    }

    if (diffDays < 0) {
        return {
            tone: 'danger',
            message: `Plano vencido há ${Math.abs(diffDays)} dia${Math.abs(diffDays) !== 1 ? 's' : ''}`,
            code,
        };
    }
    if (diffDays === 0) {
        return { tone: 'danger', message: 'Plano vence hoje', code };
    }
    if (diffDays === 1) {
        return { tone: 'warning', message: 'Plano vence amanhã', code };
    }
    if (diffDays <= 5) {
        return {
            tone: 'caution',
            message: `Vence em ${diffDays} dias · ${formattedDate}`,
            code,
        };
    }
    if (diffDays <= 10) {
        return {
            tone: 'notice',
            message: `Vence em ${diffDays} dias · ${formattedDate}`,
            code,
        };
    }
    if (diffDays <= 15) {
        return { tone: 'info', message: `Próximo vencimento: ${formattedDate}`, code };
    }

    return { tone: 'success', message: `Plano ativo até ${formattedDate}`, code };
}
